import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LockKey,
  Fire,
  Timer,
  Eye,
  Key,
  Copy,
  Warning,
  ShieldCheck,
  ArrowClockwise,
  XCircle,
} from "@phosphor-icons/react";

import { Input } from "@/components/ui/input";
import { decryptText, keyHasPassword } from "@/lib/crypto";
import { getPasteMeta, consumePaste } from "@/lib/api";

export default function ViewPaste() {
  const { id } = useParams();
  const [keyB64, setKeyB64] = useState("");
  const [meta, setMeta] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | confirm | password | decrypted | error | gone
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [plaintext, setPlaintext] = useState("");
  const [decryptedMeta, setDecryptedMeta] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    const frag = window.location.hash.replace(/^#/, "");
    setKeyB64(frag);
    if (!frag) {
      setError("Missing decryption key in URL fragment.");
      setPhase("error");
      return;
    }
    (async () => {
      try {
        const m = await getPasteMeta(id);
        setMeta(m);
        // If burn-after-read, require an explicit click before consuming.
        if (m.burn_after_read) {
          setPhase("confirm");
        } else {
          await fetchAndDecrypt(frag, null, m);
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) {
          setError("This paste no longer exists. It may have been read, expired, or never existed.");
          setPhase("gone");
        } else if (status === 410) {
          setError("This paste has expired.");
          setPhase("gone");
        } else {
          setError(err?.response?.data?.detail || "Failed to load paste.");
          setPhase("error");
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchAndDecrypt = async (keyStr, pwd, m) => {
    setWorking(true);
    try {
      const data = await consumePaste(id);
      setDecryptedMeta(data);

      const needsPwd = keyHasPassword(keyStr) || data.has_password;
      if (needsPwd && !pwd) {
        setPhase("password");
        setWorking(false);
        return;
      }

      const plain = await decryptText({
        ciphertext: data.ciphertext,
        iv: data.iv,
        keyB64: keyStr,
        password: pwd,
      });
      setPlaintext(plain);
      setPhase("decrypted");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 410) {
        setError("Paste destroyed or expired.");
        setPhase("gone");
      } else if (err?.name === "OperationError" || /decrypt/i.test(err?.message || "")) {
        setError("Decryption failed. Wrong password or corrupted link.");
        setPhase("password");
      } else {
        setError(err?.message || "Decryption error.");
        setPhase("error");
      }
    } finally {
      setWorking(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e?.preventDefault?.();
    if (!password) {
      toast.error("Enter the password.");
      return;
    }
    await fetchAndDecrypt(keyB64, password, meta);
  };

  const copyPlaintext = () => {
    navigator.clipboard.writeText(plaintext);
    toast.success("Plaintext copied to clipboard.");
  };

  // ----------------- Render -----------------

  if (phase === "loading") {
    return (
      <CenterPane>
        <div className="flex items-center gap-3 text-sm font-mono text-muted-foreground" data-testid="loading-state">
          <ArrowClockwise size={18} weight="bold" className="animate-spin" />
          Resolving encrypted record…
        </div>
      </CenterPane>
    );
  }

  if (phase === "gone" || phase === "error") {
    return (
      <CenterPane>
        <div className="border border-foreground/15 bg-card p-10 max-w-xl w-full text-center animate-fade-up" data-testid="error-state">
          <XCircle size={36} weight="bold" className="text-burn mx-auto mb-4" />
          <h2 className="font-display text-2xl font-bold tracking-tight mb-2">
            {phase === "gone" ? "This paste is gone" : "Something went wrong"}
          </h2>
          <p className="font-mono text-xs text-muted-foreground mb-6">{error}</p>
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center justify-center h-11 px-6 bg-foreground text-background text-xs font-bold uppercase tracking-[0.18em] hover:bg-neutral-800"
          >
            Create a new paste
          </Link>
        </div>
      </CenterPane>
    );
  }

  if (phase === "confirm") {
    return (
      <CenterPane>
        <div className="border border-burn bg-card p-8 max-w-xl w-full animate-fade-up" data-testid="burn-confirm">
          <div className="flex items-center gap-3 mb-3">
            <Fire size={26} weight="bold" className="text-burn" />
            <h2 className="font-display text-2xl font-bold tracking-tight">
              Burn-on-open paste
            </h2>
          </div>
          <p className="font-mono text-xs text-muted-foreground leading-relaxed mb-6">
            Opening this paste will permanently destroy it. You will get exactly
            one read. Continue only if you are the intended recipient.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/"
              data-testid="burn-cancel"
              className="h-11 border border-foreground inline-flex items-center justify-center text-xs font-bold uppercase tracking-[0.18em] hover:bg-secondary"
            >
              Cancel
            </Link>
            <button
              data-testid="burn-confirm-btn"
              onClick={() => fetchAndDecrypt(keyB64, null, meta)}
              disabled={working}
              className="h-11 bg-burn text-white inline-flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.18em] hover:opacity-90 disabled:opacity-50"
            >
              <Fire size={14} weight="bold" /> Read & Burn
            </button>
          </div>
        </div>
      </CenterPane>
    );
  }

  if (phase === "password") {
    return (
      <CenterPane>
        <form
          onSubmit={handlePasswordSubmit}
          className="border border-foreground/15 bg-card p-8 max-w-md w-full animate-fade-up"
          data-testid="password-form"
        >
          <div className="flex items-center gap-3 mb-3">
            <Key size={24} weight="bold" />
            <h2 className="font-display text-2xl font-bold tracking-tight">
              Password required
            </h2>
          </div>
          <p className="font-mono text-xs text-muted-foreground mb-5">
            This paste is sealed with an additional password. Enter it to
            decrypt.
          </p>
          {error && (
            <div
              data-testid="password-error"
              className="border border-burn bg-burn/5 text-burn font-mono text-xs p-3 mb-4"
            >
              {error}
            </div>
          )}
          <Input
            data-testid="decrypt-password-input"
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="paste password"
            className="rounded-none font-mono h-11 border-foreground/30 mb-4"
          />
          <button
            type="submit"
            data-testid="decrypt-btn"
            disabled={working}
            className="h-11 w-full bg-foreground text-background text-xs font-bold uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2 hover:bg-neutral-800 disabled:bg-neutral-400"
          >
            <LockKey size={14} weight="bold" />
            {working ? "Decrypting…" : "Decrypt paste"}
          </button>
        </form>
      </CenterPane>
    );
  }

  // decrypted
  const burned = decryptedMeta?.burn_after_read;
  const viewsLeft =
    decryptedMeta?.view_limit != null
      ? Math.max(0, decryptedMeta.view_limit - decryptedMeta.views)
      : null;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-8 py-10 animate-fade-up">
      <div className="mb-6 flex items-center gap-3">
        <ShieldCheck size={22} weight="bold" className="text-secure" />
        <span className="label-overline" data-testid="decrypted-label">
          Decrypted locally · plaintext never reached the server
        </span>
      </div>

      {burned && (
        <div
          data-testid="burn-banner"
          className="mb-6 border border-burn bg-burn/5 p-4 flex items-start gap-3"
        >
          <Fire size={18} weight="bold" className="text-burn flex-shrink-0 mt-0.5" />
          <p className="font-mono text-xs leading-relaxed">
            <span className="font-bold text-burn">PASTE DESTROYED.</span> This
            was a burn-after-read paste. The ciphertext has been wiped from the
            server. Copy the content now — this URL will no longer work.
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Plaintext */}
        <div className="lg:col-span-3 border border-foreground/15 bg-card">
          <div className="border-b border-foreground/15 px-4 py-3 flex items-center justify-between">
            <span className="label-overline flex items-center gap-2">
              <LockKey size={12} weight="bold" /> Decrypted content
              {decryptedMeta?.language && (
                <span className="ml-2 px-2 py-0.5 bg-foreground text-background tracking-[0.18em]">
                  {decryptedMeta.language}
                </span>
              )}
            </span>
            <button
              data-testid="copy-content-btn"
              onClick={copyPlaintext}
              className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] border border-foreground/25 px-3 h-8 hover:bg-secondary"
            >
              <Copy size={12} weight="bold" /> Copy
            </button>
          </div>
          <pre
            data-testid="decrypted-content"
            className="p-5 font-mono text-sm whitespace-pre-wrap break-words overflow-auto min-h-[40vh] max-h-[70vh]"
          >
            {plaintext}
          </pre>
        </div>

        {/* Side */}
        <aside className="border border-foreground/15 bg-card divide-y divide-foreground/10">
          <SideRow
            icon={<Timer size={14} weight="bold" />}
            label="Expires"
            value={
              decryptedMeta?.expires_at
                ? new Date(decryptedMeta.expires_at).toLocaleString()
                : "Never"
            }
            testid="view-expires"
          />
          <SideRow
            icon={<Eye size={14} weight="bold" />}
            label="Views"
            value={
              decryptedMeta?.view_limit != null
                ? `${decryptedMeta.views} / ${decryptedMeta.view_limit}`
                : `${decryptedMeta?.views ?? 1}`
            }
            testid="view-counter"
          />
          {viewsLeft != null && (
            <SideRow
              icon={<Warning size={14} weight="bold" />}
              label="Reads remaining"
              value={viewsLeft === 0 ? "destroyed" : `${viewsLeft}`}
              accent={viewsLeft === 0 ? "burn" : null}
              testid="reads-remaining"
            />
          )}
          <SideRow
            icon={<Fire size={14} weight="bold" />}
            label="Burn after read"
            value={decryptedMeta?.burn_after_read ? "Yes" : "No"}
            accent={decryptedMeta?.burn_after_read ? "burn" : null}
            testid="view-burn"
          />
          <SideRow
            icon={<Key size={14} weight="bold" />}
            label="Paste ID"
            value={id}
            testid="view-id"
          />
        </aside>
      </div>

      <div className="mt-10 flex items-center justify-between">
        <Link
          to="/"
          data-testid="new-paste-link"
          className="text-xs font-mono uppercase tracking-[0.18em] border-b border-foreground hover:opacity-60"
        >
          + Create new paste
        </Link>
      </div>
    </div>
  );
}

function SideRow({ icon, label, value, accent, testid }) {
  return (
    <div className="p-5" data-testid={testid}>
      <div className="label-overline flex items-center gap-2">
        {icon} {label}
      </div>
      <div
        className={`mt-2 font-mono text-sm break-all ${
          accent === "burn" ? "text-burn font-bold" : "text-foreground"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function CenterPane({ children }) {
  return (
    <div className="max-w-6xl mx-auto px-6 md:px-8 py-20 flex items-center justify-center">
      {children}
    </div>
  );
}
